import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API_BASE from '../../utils/api.js';
import logoImg from '../../assets/default-if-logo.png';
import '../../component-styles/ReportsShared.css';

const PLAN_LABELS = { premium: 'Premium', standard: 'Standard' };
const INTERVAL_LABELS = { month: 'Monthly', year: 'Annual' };
const INTERVAL_SUFFIX = { month: '/mo', year: '/yr' };
const REVENUE_NOTE =
  'Recurring revenue is net of ongoing discounts, counts annual plans at 1/12 per month, and leaves out trials.';

// PDF widths: `width` is fixed (mm) so dates and amounts never wrap; the text
// columns split whatever page width is left in proportion to `flex`.
const COLUMNS = [
  { key: 'company', label: 'Company', flex: 1 },
  { key: 'email', label: 'Email', flex: 1 },
  { key: 'plan', label: 'Plan', width: 19 },
  { key: 'billing', label: 'Billing', width: 19 },
  { key: 'amount', label: 'Amount', width: 33, align: 'right' },
  { key: 'status', label: 'Status', width: 29 },
  { key: 'since', label: 'Subscriber Since', width: 24 },
  { key: 'renews', label: 'Renews', width: 23 },
  { key: 'seats', label: 'Team Seats', width: 17, align: 'right' },
];
const CONTACT_COLUMN = { key: 'contact', label: 'Contact', flex: 0.7 };

/* --------------------------------------------------------
   Formatting
-------------------------------------------------------- */
function formatMoney(cents, currency = 'usd') {
  return (Number(cents || 0) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: String(currency).toUpperCase(),
  });
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function amountLabel(row) {
  const amount = `${formatMoney(row.amountCents, row.currency)}${INTERVAL_SUFFIX[row.interval] || ''}`;
  return row.discountLabel ? `${amount} (${row.discountLabel})` : amount;
}

function statusLabel(row) {
  if (row.status === 'trialing')
    return row.trialEnd ? `Trial until ${formatDate(row.trialEnd)}` : 'Trial';
  if (row.status === 'past_due') return 'Past due';
  if (row.cancelAt) return `Cancels ${formatDate(row.cancelAt)}`;
  return 'Active';
}

function statusBadgeClass(row) {
  if (row.status === 'past_due') return 'status-overdue';
  if (row.status === 'trialing') return 'status-sent';
  if (row.cancelAt) return 'status-partial';
  return 'status-paid';
}

function toCells(row) {
  return {
    company: row.companyName || row.contactName || '-',
    contact: row.companyName ? row.contactName : '',
    email: row.email || '-',
    plan: PLAN_LABELS[row.plan] || row.plan || '-',
    billing: INTERVAL_LABELS[row.interval] || '-',
    amount: amountLabel(row),
    status: statusLabel(row),
    since: formatDate(row.subscribedSince),
    renews: row.cancelAt ? '-' : formatDate(row.currentPeriodEnd),
    seats: String(row.teamSeats || 0),
  };
}

function summarize(rows) {
  const count = (predicate) => rows.filter(predicate).length;
  return {
    total: rows.length,
    premium: count((r) => r.plan === 'premium'),
    standard: count((r) => r.plan === 'standard'),
    monthly: count((r) => r.interval === 'month'),
    annual: count((r) => r.interval === 'year'),
    trialing: count((r) => r.status === 'trialing'),
    pastDue: count((r) => r.status === 'past_due'),
    canceling: count((r) => r.cancelAt),
    teamSeats: rows.reduce((sum, r) => sum + (r.teamSeats || 0), 0),
    mrrCents: rows.reduce((sum, r) => sum + (r.monthlyCents || 0), 0),
    currency: rows[0]?.currency || 'usd',
  };
}

function summaryTiles(summary) {
  return [
    ['Subscribers', String(summary.total)],
    ['Premium', String(summary.premium)],
    ['Standard', String(summary.standard)],
    ['Monthly recurring revenue', formatMoney(summary.mrrCents, summary.currency)],
    ['Annual run rate', formatMoney(summary.mrrCents * 12, summary.currency)],
  ];
}

function summaryDetails(summary) {
  return [
    `${summary.monthly} monthly`,
    `${summary.annual} annual`,
    summary.trialing ? `${summary.trialing} in trial` : null,
    summary.pastDue ? `${summary.pastDue} past due` : null,
    summary.canceling ? `${summary.canceling} canceling` : null,
    `${summary.teamSeats} team seat${summary.teamSeats === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join('  ·  ');
}

/* --------------------------------------------------------
   PDF helpers
-------------------------------------------------------- */
async function loadLogoForPDF(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('timeout')), 6000);
    img.onload = () => {
      clearTimeout(timer);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      try {
        resolve({
          data: canvas.toDataURL('image/png'),
          aspect: img.naturalWidth / img.naturalHeight,
        });
      } catch {
        reject(new Error('canvas tainted'));
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('load error'));
    };
    img.src = url;
  });
}

export default function SubscriberReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfExporting, setPdfExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/support-admin/subscribers`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load subscribers');
      setReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => report?.subscribers || [], [report]);
  const summary = useMemo(() => summarize(rows), [rows]);

  async function exportPDF() {
    setPdfExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      let logo = null;
      try {
        logo = await loadLogoForPDF(logoImg);
      } catch {}

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.width;
      const pageH = doc.internal.pageSize.height;
      const ML = 14;
      const MR = 14;
      const contentW = pageW - ML - MR;

      // ── Brand colours (shared with the other report PDFs) ──────
      const C_CHARCOAL = [17, 24, 39];
      const C_HEADER_BG = [98, 121, 78];
      const C_TEAL = [165, 210, 125];
      const C_WHITE = [255, 255, 255];
      const C_OFF_WHITE = [247, 247, 245];
      const C_BORDER = [229, 231, 235];
      const C_MUTED = [107, 114, 128];

      // ── Header ─────────────────────────────────────────────────
      if (logo) {
        const LOGO_H = 12;
        const LOGO_W = LOGO_H * logo.aspect;
        // The logo source is 2658px wide and jsPDF embeds PNGs uncompressed by
        // default — without a compression level (and an alias, so later pages
        // reuse the same image) it adds over 5MB to the file.
        doc.addImage(logo.data, 'PNG', pageW - MR - LOGO_W, 7, LOGO_W, LOGO_H, 'logo', 'FAST');
      }

      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...C_CHARCOAL);
      doc.text('Current Subscribers', ML, 15);

      doc.setFontSize(8.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...C_MUTED);
      doc.text(`Live from Stripe as of ${formatDateTime(report?.generatedAt)}`, ML, 22);

      doc.setDrawColor(...C_TEAL);
      doc.setLineWidth(0.6);
      doc.line(ML, 27, pageW - MR, 27);

      // ── Summary tiles ──────────────────────────────────────────
      const tiles = summaryTiles(summary);
      const TILE_GAP = 4;
      const TILE_H = 18;
      const TILE_Y = 31;
      const tileW = (contentW - TILE_GAP * (tiles.length - 1)) / tiles.length;
      tiles.forEach(([label, value], i) => {
        const x = ML + i * (tileW + TILE_GAP);
        doc.setFillColor(...C_OFF_WHITE);
        doc.setDrawColor(...C_BORDER);
        doc.setLineWidth(0.2);
        doc.roundedRect(x, TILE_Y, tileW, TILE_H, 2, 2, 'FD');
        doc.setFontSize(7.5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C_MUTED);
        doc.text(label, x + 4, TILE_Y + 6);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C_CHARCOAL);
        doc.text(value, x + 4, TILE_Y + 14);
      });

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...C_MUTED);
      doc.text(summaryDetails(summary), ML, TILE_Y + TILE_H + 6);
      doc.text(REVENUE_NOTE, ML, TILE_Y + TILE_H + 10.5);

      // ── Table ──────────────────────────────────────────────────
      // Most accounts have no contact name on file, so the column only takes
      // up width when at least one row has one.
      const body = rows.map(toCells);
      const columns = body.some((cells) => cells.contact)
        ? [COLUMNS[0], CONTACT_COLUMN, ...COLUMNS.slice(1)]
        : COLUMNS;
      const fixedW = columns.reduce((sum, c) => sum + (c.width || 0), 0);
      const totalFlex = columns.reduce((sum, c) => sum + (c.flex || 0), 0);
      const columnStyles = Object.fromEntries(
        columns.map((c) => [
          c.key,
          {
            cellWidth: c.width || ((contentW - fixedW) * c.flex) / totalFlex,
            ...(c.align ? { halign: c.align } : {}),
          },
        ])
      );
      const PAD = 2.5;

      autoTable(doc, {
        startY: TILE_Y + TILE_H + 15,
        columns: columns.map((c) => ({ header: c.label, dataKey: c.key })),
        body,
        columnStyles,
        rowPageBreak: 'avoid',
        margin: { top: 24, left: ML, right: MR, bottom: 16 },
        styles: {
          font: 'helvetica',
          fontSize: 8,
          cellPadding: { top: PAD, right: PAD, bottom: PAD, left: PAD },
          overflow: 'linebreak',
          valign: 'middle',
          textColor: C_CHARCOAL,
          lineColor: C_BORDER,
          lineWidth: 0.15,
        },
        headStyles: {
          fillColor: C_HEADER_BG,
          textColor: C_WHITE,
          fontStyle: 'bold',
          fontSize: 7.5,
        },
        bodyStyles: { fillColor: C_WHITE },
        alternateRowStyles: { fillColor: C_OFF_WHITE },
      });

      // ── Footer on every page ───────────────────────────────────
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        if (i > 1) {
          if (logo) {
            const LOGO_H = 12;
            const LOGO_W = LOGO_H * logo.aspect;
            doc.addImage(logo.data, 'PNG', pageW - MR - LOGO_W, 5, LOGO_W, LOGO_H, 'logo', 'FAST');
          }
          doc.setDrawColor(...C_TEAL);
          doc.setLineWidth(0.6);
          doc.line(ML, 19, pageW - MR, 19);
        }

        doc.setDrawColor(...C_BORDER);
        doc.setLineWidth(0.2);
        doc.line(ML, pageH - 12, pageW - MR, pageH - 12);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C_MUTED);
        doc.text('InteriorFolio — Confidential', ML, pageH - 7);
        doc.text(`Page ${i} of ${pageCount}`, pageW - MR, pageH - 7, { align: 'right' });
      }

      // Local date, not toISOString() — that's UTC, which stamps an evening
      // export in the US with tomorrow's date.
      const today = new Date();
      const stamp = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, '0'),
        String(today.getDate()).padStart(2, '0'),
      ].join('-');
      doc.save(`interiorfolio-subscribers-${stamp}.pdf`);
    } finally {
      setPdfExporting(false);
    }
  }

  return (
    <div className="report-shell-inner subscriber-report">
      <div className="report-header">
        <div className="report-title">
          <h2>Current Subscribers</h2>
          <div className="muted">
            {report?.generatedAt
              ? `Live from Stripe as of ${formatDateTime(report.generatedAt)}. `
              : ''}
            Only support accounts can see this report.
          </div>
        </div>
        <div className="report-actions">
          <button type="button" className="report-btn-secondary" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="report-btn-primary"
            onClick={exportPDF}
            disabled={loading || pdfExporting || rows.length === 0}
          >
            {pdfExporting ? 'Building PDF…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="report-card report-empty">{error}</div>
      ) : !report ? (
        <div className="report-card report-loading-card">
          <div className="report-loading-text">Loading subscribers…</div>
        </div>
      ) : (
        <>
          <div className="report-card">
            <div className="report-metrics">
              {summaryTiles(summary).map(([label, value]) => (
                <div className="report-metric" key={label}>
                  <span className="report-metric-value">{value}</span>
                  <span className="report-metric-label">{label}</span>
                </div>
              ))}
            </div>
            <span className="report-subtext">{summaryDetails(summary)}</span>
            <span className="report-subtext">{REVENUE_NOTE}</span>
          </div>

          <div className="report-table-wrap">
            <table className="report-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={c.align === 'right' ? 'right' : undefined}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="report-empty">
                      No current subscribers.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const cells = toCells(row);
                    return (
                      <tr key={row.stripeSubscriptionId}>
                        <td>
                          {cells.company}
                          {cells.contact ? (
                            <span className="report-subtext">{cells.contact}</span>
                          ) : null}
                        </td>
                        <td>{cells.email}</td>
                        <td>{cells.plan}</td>
                        <td>{cells.billing}</td>
                        <td className="right">{cells.amount}</td>
                        <td>
                          <span className={`report-badge ${statusBadgeClass(row)}`}>
                            {cells.status}
                          </span>
                        </td>
                        <td>{cells.since}</td>
                        <td>{cells.renews}</td>
                        <td className="right">{cells.seats}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
